/**
 * Auto-detect low-end devices and apply a CSS class to <html> so that
 * heavy visual effects (backdrop-blur, infinite animations, large blurs)
 * gracefully downgrade.
 *
 * Heuristics (any one of these triggers low-perf mode):
 *  - prefers-reduced-motion: reduce
 *  - navigator.deviceMemory < 4 GB
 *  - navigator.hardwareConcurrency <= 4
 *  - Save-Data hint
 *  - Slow connection (2g / 3g / slow-2g)
 *  - User opted-in via localStorage (manual toggle)
 *
 * The user can manually toggle via:
 *   localStorage.setItem('vdh_perf_mode', 'low')   // force on
 *   localStorage.setItem('vdh_perf_mode', 'high')  // force off
 *   localStorage.removeItem('vdh_perf_mode')       // back to auto
 */
const STORAGE_KEY = "vdh_perf_mode";

export function detectLowPerf() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  // Manual override
  try {
    const manual = localStorage.getItem(STORAGE_KEY);
    if (manual === "low") return true;
    if (manual === "high") return false;
  } catch {
    /* ignore */
  }

  // Reduced motion = always low-perf
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return true;
    }
  } catch {
    /* ignore */
  }

  // Save-Data hint from Chromium / browsers
  if (navigator.connection && navigator.connection.saveData) {
    return true;
  }

  // Slow connection
  if (navigator.connection && navigator.connection.effectiveType) {
    const eff = navigator.connection.effectiveType;
    if (eff === "slow-2g" || eff === "2g" || eff === "3g") return true;
  }

  // RAM (Chrome only). Apple Silicon Air with 8GB returns 8.
  // We trigger low-perf only for genuinely constrained devices.
  if (typeof navigator.deviceMemory === "number" && navigator.deviceMemory > 0) {
    if (navigator.deviceMemory < 4) return true;
  }

  // CPU cores
  if (
    typeof navigator.hardwareConcurrency === "number" &&
    navigator.hardwareConcurrency > 0 &&
    navigator.hardwareConcurrency <= 2
  ) {
    return true;
  }

  // Old-school mobile devices identified via UA (not a perf signal in itself,
  // but combined with low cores/memory it's a strong hint). Skip — already
  // covered by the above heuristics.

  return false;
}

export function applyPerfClass() {
  if (typeof document === "undefined") return false;
  const low = detectLowPerf();
  document.documentElement.classList.toggle("vdh-low-perf", low);
  document.documentElement.dataset.perfMode = low ? "low" : "high";
  return low;
}

/** Manually override the auto-detection. */
export function setPerfMode(mode /* 'auto' | 'low' | 'high' */) {
  try {
    if (mode === "auto") {
      localStorage.removeItem(STORAGE_KEY);
    } else if (mode === "low" || mode === "high") {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  } catch {
    /* ignore */
  }
  applyPerfClass();
}
